import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "@/components/base/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import { getMovieById, getRelatedMovies } from "@/mocks/movies";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(Number(id));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-200 mx-auto mb-4">
              <i className="ri-film-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">未找到该影视</h1>
            <p className="text-foreground-500 mb-6">该内容可能已被移除或链接失效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedMovies = getRelatedMovies(movie);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Movie",
    name: movie.title,
    description: movie.summary,
    image: movie.coverImage,
    dateCreated: `${movie.year}`,
    director: {
      "@type": "Person",
      name: movie.director,
    },
    actor: movie.cast.map((name) => ({
      "@type": "Person",
      name,
    })),
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: movie.rating,
      bestRating: "10",
      ratingCount: movie.viewCount,
    },
  };

  return (
    <>
      <Helmet>
        <title>{movie.title} - 酷客影院 | 高清在线观看</title>
        <meta name="description" content={`${movie.title}（${movie.year}）${movie.summary}由${movie.director}执导，${movie.cast.join("、")}主演。酷客影院提供${movie.title}高清在线观看，超清画质不缓冲。`} />
        <meta name="keywords" content={`${movie.title},${movie.category},高清在线观看,超清播放,酷客影院`} />
        <link rel="canonical" href={`https://www.kukecinema.com/detail/${movie.id}`} />
        <meta property="og:title" content={`${movie.title} - 酷客影院`} />
        <meta property="og:description" content={movie.summary} />
        <meta property="og:image" content={movie.coverImage} />
        <meta property="og:type" content="video.movie" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${movie.title} - 酷客影院`} />
        <meta name="twitter:description" content={movie.summary} />
        <meta name="twitter:image" content={movie.coverImage} />
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="px-4 md:px-6 py-6">
            <div className="max-w-screen-2xl mx-auto">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  { label: movie.category, href: `/category/${movie.categorySlug}` },
                  { label: movie.title },
                ]}
              />
            </div>
          </div>

          <section className="relative w-full h-[280px] md:h-[400px] overflow-hidden">
            <img
              src={movie.detailImage}
              alt={`${movie.title} - 横幅海报`}
              title={`${movie.title} - 酷客影院`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent"></div>
          </section>

          <section className="px-4 md:px-6 -mt-32 md:-mt-40 relative z-10 pb-10">
            <div className="max-w-screen-2xl mx-auto">
              <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                <div className="w-40 md:w-56 shrink-0 mx-auto md:mx-0">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200 shadow-lg">
                    <img
                      src={movie.coverImage}
                      alt={movie.title}
                      title={`${movie.title} - 高清在线观看`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950">
                    {movie.title}
                  </h1>
                  <div className="flex items-center gap-3 mt-3 flex-wrap">
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-accent-100 text-accent-700 text-sm font-medium">
                      <i className="ri-star-fill"></i>
                      {movie.rating}
                    </span>
                    <span className="text-sm text-foreground-500">{movie.year}</span>
                    <span className="text-sm text-foreground-500">{movie.duration}</span>
                    <span className="text-sm text-foreground-500">{movie.region}</span>
                    <span className="text-sm px-2 py-0.5 rounded bg-secondary-100 text-secondary-700 whitespace-nowrap">
                      {movie.category}
                    </span>
                  </div>

                  <div className="mt-5 space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <span className="text-foreground-400 shrink-0">导演：</span>
                      <span className="text-foreground-800">{movie.director}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-foreground-400 shrink-0">主演：</span>
                      <span className="text-foreground-800">{movie.cast.join(" / ")}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-foreground-400 shrink-0">类型：</span>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {movie.tags.map((tag) => (
                          <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-background-200 text-foreground-600 whitespace-nowrap">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 flex items-center gap-3 flex-wrap">
                    <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-play-fill text-lg"></i>
                      立即播放
                    </button>
                    <button className="inline-flex items-center gap-2 px-4 py-2.5 bg-background-200 hover:bg-background-300 text-foreground-700 font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-heart-line"></i>
                      收藏
                    </button>
                    <button className="inline-flex items-center gap-2 px-4 py-2.5 bg-background-200 hover:bg-background-300 text-foreground-700 font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-share-forward-line"></i>
                      分享
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-8 md:py-10 bg-background-100">
            <div className="max-w-screen-2xl mx-auto">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">剧情简介</h2>
              <p className="text-foreground-700 leading-relaxed text-sm md:text-base max-w-4xl">
                {movie.description}
              </p>
            </div>
          </section>

          {relatedMovies.length > 0 && (
            <section className="px-4 md:px-6 py-10 md:py-14">
              <div className="max-w-screen-2xl mx-auto">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}